/**
 * Body scroll lock with a MODULE-LEVEL reference count.
 *
 * Every modal used to do this by hand:
 *
 *   const prev = document.body.style.overflow;
 *   document.body.style.overflow = 'hidden';
 *   return () => { document.body.style.overflow = prev; };
 *
 * which is correct for exactly one modal and wrong for two. Open a nested
 * modal and the inner one saves `'hidden'` — the *outer* modal's value — as
 * "what it was before". Close them in the wrong order and the inner one's
 * cleanup restores `''` while the outer modal is still up (page scrolls
 * behind the overlay); close them in the right order and the outer one
 * restores the inner one's captured `'hidden'`, permanently wedging the page
 * unscrollable with no modal on screen and no way back short of a reload.
 *
 * It also broke without nesting. A per-modal effect whose dependency array
 * includes changing state (InboxAddLineModal listed `busy`, `quickAdd`,
 * `zeroRateConfirmOpen`) tears down and re-runs on every one of those
 * changes — re-capturing "previous" overflow from whatever is current, which
 * after the first run is `'hidden'`.
 *
 * The fix is to stop treating "is the body locked" as per-modal state. One
 * counter owns it:
 *
 *   - 0 → 1  save the real pre-modal overflow, set 'hidden'
 *   - n → n+1  nothing (already hidden; do NOT re-save)
 *   - 1 → 0  restore the saved value
 *
 * Nesting is safe and close order is irrelevant, because the saved value is
 * only ever captured when nothing else holds the lock.
 */
import { useEffect } from 'react';

/**
 * The slice of an element we actually touch. Narrow on purpose: it makes the
 * refcount unit-testable with a plain object in the Node test environment,
 * where there is no `document`.
 */
export interface ScrollLockTarget {
  style: { overflow: string };
}

let lockCount = 0;
let savedOverflow: string | null = null;

/** Take a lock. Hides overflow only on the 0 → 1 transition. */
export function acquireScrollLock(target: ScrollLockTarget): void {
  lockCount += 1;
  if (lockCount === 1) {
    savedOverflow = target.style.overflow;
    target.style.overflow = 'hidden';
  }
}

/** Release a lock. Restores the saved overflow only on the 1 → 0 transition. */
export function releaseScrollLock(target: ScrollLockTarget): void {
  // Defensive: an unbalanced release must never drive the count negative, or
  // the next acquire would fail to hide overflow at all.
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount === 0) {
    target.style.overflow = savedOverflow ?? '';
    savedOverflow = null;
  }
}

/** Current holder count. Exported for assertions in tests. */
export function getScrollLockCount(): number {
  return lockCount;
}

/** Reset module state between tests. Not for application use. */
export function resetScrollLockForTests(): void {
  lockCount = 0;
  savedOverflow = null;
}

/**
 * Lock body scroll while `locked` is true.
 *
 * Gate it on the modal's open state and nothing else — passing values that
 * change during the modal's life (busy flags, nested-dialog state) reintroduces
 * the churn this hook exists to eliminate.
 *
 *   useBodyScrollLock(open);
 *   useBodyScrollLock(true);      // component only mounts when open
 */
export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    acquireScrollLock(document.body);
    return () => releaseScrollLock(document.body);
  }, [locked]);
}
